/**
 * Global `fetch` interceptor that auto-injects the admin password
 * header on every request whose URL contains `/api/admin/` (and on
 * the whitelisted mutation endpoints listed in `SENSITIVE_PATHS`).
 *
 * This lets us protect dozens of admin API call sites without
 * updating each one individually — the backend `require_admin`
 * dependency (see backend/utils.py) checks for `X-Admin-Password`
 * and rejects the request when `ADMIN_AUTH_STRICT=true` in the
 * backend env.
 *
 * Idempotent: installs the wrapper only once per page.
 */
const ORIGINAL_FETCH_KEY = '__rcm_fetch_intercepted__';
const SENSITIVE_MARKERS = [
  '/api/admin/',
  '/api/promotions/purge-broken', // internal cleanup, admin-only
];

export function installAdminFetchInterceptor() {
  if (typeof window === 'undefined') return;
  if (window[ORIGINAL_FETCH_KEY]) return; // already installed
  const originalFetch = window.fetch.bind(window);
  window[ORIGINAL_FETCH_KEY] = originalFetch;

  window.fetch = (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = String(init.method || (typeof input !== 'string' && input?.method) || 'GET').toUpperCase();
      const needsAuth = SENSITIVE_MARKERS.some(m => url.includes(m))
        || (method !== 'GET' && /\/api\/products(\/|$|\?)/.test(url));
      if (needsAuth) {
        const secret = sessionStorage.getItem('rcm_admin_secret');
        if (secret) {
          const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
          if (!headers.has('X-Admin-Password')) headers.set('X-Admin-Password', secret);
          return originalFetch(input, { ...init, headers });
        }
      }
    } catch (e) {
      // Never break the app on interceptor failure — fall through
      // to the original request.
      console.warn('[adminFetch] interceptor error', e);
    }
    return originalFetch(input, init);
  };
}
